// The Catalog tab of the Project Datasheet Composer: browse a source (the master catalog, or
// another project database the user can open) and pull its Index Sheets into this database, with
// their manufacturer, datasheet and values. A sheet already here can be updated from the source,
// and a datasheet that differs is compared in the difference viewer, from where the source's
// revision is adopted or a new revision with the source's values is made. Every call needs both
// utilities.ds.project.composer and utilities.ds.project.catalog on this database.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Download, Eye, RotateCcw } from 'lucide-react';
import { dsProjectCatalogApi, dsProjectApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import { applySearch, SEARCH_HINT } from '../../utils/listSearch';
import { linesToBoxes, toIndexSide } from './dsBoxes';
import { sheetLayout } from '../development/dsTemplateLayout';
import { useDsTemplates } from '../development/useDsTemplates';
import DsDiffViewerModal from './DsDiffViewerModal';
import { PullPreviewModal, PullResultsModal, UpdateFromSourceModal } from './ProjectCatalogModals';
import { statusBadgeClass, alignRevision } from './masterCatalog';
import { errText } from './projectSheets';
import {
  stateBadgeClass, stateLabel, FILTERS, filterRows, isPullable, canUpdate, canCompareDatasheet,
  DATASHEET_ACTION, keepPullable,
} from './projectCatalog';
import StuiSelect from '../../components/common/StuiSelect';

const SEARCH_FIELDS = ['indexSheet', 'description', 'manufacturerCode', 'type', 'status', 'dsIndex', 'state'];

export default function ProjectCatalogTab() {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };
  const specRows = useDsTemplates(dsProjectApi.getLayoutSpec);

  const [sources, setSources] = useState([]);
  const [source, setSource] = useState('catalog');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(() => new Set());
  const [bringValues, setBringValues] = useState(true);
  const [dialog, setDialog] = useState(null);   // { kind: 'pull' | 'results' | 'update', ... }
  const [diff, setDiff] = useState(null);       // DsDiffViewerModal props + row and action
  const [busy, setBusy] = useState(false);

  const sourceLabel = sources.find(s => s.name === source)?.displayName || source;

  useEffect(() => {
    dsProjectCatalogApi.getSources()
      .then(r => setSources(r.data ?? []))
      .catch(e => notify.error(errText(e, 'Failed to load the sources.')));
  }, [notify]);

  const load = useCallback(async () => {
    if (!source) return;
    setLoading(true);
    try {
      const res = await dsProjectCatalogApi.browse(source);
      const list = res.data?.sheets ?? [];
      setRows(list);
      setSelected(prev => keepPullable(prev, list));
    } catch (e) {
      setRows([]);
      notify.error(errText(e, 'Failed to load the source.'));
    } finally { setLoading(false); }
  }, [source, notify]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filterRows(rows, filter), [rows, filter]);
  const shown = useMemo(() => applySearch(filtered, search, SEARCH_FIELDS), [filtered, search]);
  const shownPullable = shown.filter(isPullable);
  const allShownPicked = shownPullable.length > 0 && shownPullable.every(r => selected.has(r.sourceId));

  const toggle = id => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllShown = () => setSelected(prev => {
    const next = new Set(prev);
    shownPullable.forEach(r => (allShownPicked ? next.delete(r.sourceId) : next.add(r.sourceId)));
    return next;
  });

  const handlePull = async () => {
    const ids = [...selected];
    setBusy(true);
    try {
      const res = await dsProjectCatalogApi.pull(source, ids, bringValues, auditCfg);
      setDialog({ kind: 'results', results: res.data?.results ?? [] });
      setSelected(new Set());
      await load();
    } catch (e) { notify.error(errText(e, 'Pull failed.')); }
    finally { setBusy(false); }
  };

  const handleUpdate = async row => {
    setBusy(true);
    try {
      const res = await dsProjectCatalogApi.updateFromSource(source, row.sourceId, auditCfg);
      const d = res.data ?? {};
      notify.success(`${row.indexSheet} updated: ${(d.changed ?? []).map(c => c.field).join(', ') || 'nothing changed'}.`);
      (d.warnings ?? []).forEach(w => notify.warning(w));
      setDialog(null);
      await load();
    } catch (e) { notify.error(errText(e, 'Update failed.')); }
    finally { setBusy(false); }
  };

  // This database on the left, the source on the right. Two revisions share no box IDs, so the
  // source side is lined up with this side's boxes by place.
  const openDiff = async row => {
    try {
      const { data } = await dsProjectCatalogApi.sheetDiff(source, row.sourceId);
      const side = s => (s ? linesToBoxes(s.lines ?? []).map(toIndexSide) : []);
      const left = side(data.target);
      const aligned = alignRevision(left, side(data.source), data.source?.values ?? {});
      const rec = data.source ?? data.target;
      const sheetCount = Math.max(1, rec?.sheetCount ?? 1);
      if (data.actionBlocker) notify.warning(data.actionBlocker);
      setDiff({
        row,
        action: DATASHEET_ACTION[data.kind] ?? null,
        blocker: data.actionBlocker ?? null,
        title: `${row.indexSheet}: this database vs ${sourceLabel}`,
        leftLabel: data.target ? `This database (rev ${data.target.rev ?? '?'})` : 'This database (no datasheet)',
        rightLabel: data.source ? `${sourceLabel} (rev ${data.source.rev ?? '?'})` : `${sourceLabel} (none)`,
        leftBoxes: left,
        leftEmptyNote: "No datasheet in this database; pulling brings the source's.",
        rightBoxes: aligned.boxes,
        leftValues: data.target?.values ?? {},
        rightValues: aligned.values,
        sheetCount,
        layout: sheetLayout(specRows, rec?.tempType ?? null, 1, sheetCount, rec?.dsName ?? null),
      });
    } catch (e) { notify.error(errText(e, 'Failed to load the datasheets.')); }
  };

  const handleDatasheetAction = async () => {
    const { row, action } = diff;
    setBusy(true);
    try {
      if (action.id === 'adopt') {
        const { data: d } = await dsProjectCatalogApi.adoptRevision(source, row.sourceId, auditCfg);
        notify.success(`${row.indexSheet} now uses ${d.dsIndex}` +
          (d.broughtIntoProject ? ' (copied into this database)' : '') +
          ` with the source's values (${d.values.inserted} added, ${d.values.updated} changed, ${d.values.deleted} removed).`);
      } else {
        const { data: d } = await dsProjectCatalogApi.newRevision(source, row.sourceId, auditCfg);
        notify.success(`${d.dsIndex} created; ${row.indexSheet} moved to it with ${d.valuesLoaded} source value${d.valuesLoaded === 1 ? '' : 's'}. ${d.previous} stays as history.`);
      }
      setDiff(null);
      await load();
    } catch (e) { notify.error(errText(e, `${action.label} failed.`)); }
    finally { setBusy(false); }
  };

  const pickedRows = rows.filter(r => selected.has(r.sourceId));

  return (
    <>
      <div className="stui-page-header">
        <div className="stui-row-8">
          <label className="stui-label" htmlFor="ds-catalog-source">Source</label>
          <StuiSelect id="ds-catalog-source" className="stui-select" value={source} disabled={loading || busy}
            onChange={e => { setSource(e.target.value); setSelected(new Set()); }}>
            {(sources.length ? sources : [{ name: 'catalog', displayName: 'Master catalog' }]).map(s => (
              <option key={s.name} value={s.name}>{s.displayName || s.name}</option>
            ))}
          </StuiSelect>
          <div className="stui-segmented">
            {FILTERS.map(([id, label]) => (
              <button key={id} className={`stui-segmented-btn${filter === id ? ' selected' : ''}`} onClick={() => setFilter(id)}>
                {`${label} (${filterRows(rows, id).length})`}
              </button>
            ))}
          </div>
        </div>
        <div className="stui-row-8">
          <label className="stui-checkbox">
            <input type="checkbox" className="stui-checkbox-input" checked={bringValues} onChange={e => setBringValues(e.target.checked)} />
            Bring values
          </label>
          <button className="stui-btn stui-btn-primary" disabled={selected.size === 0 || busy}
            onClick={() => setDialog({ kind: 'pull' })}>
            <Download size={14} /> {`Pull selected (${selected.size})`}
          </button>
          <div className="stui-search-wrapper">
            <SearchClearIcon size={14} searchTerm={search} onClear={() => setSearch('')} />
            <input className="stui-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." title={SEARCH_HINT} />
          </div>
          <button className="stui-btn stui-btn-secondary" onClick={load} disabled={loading} title="Refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className="stui-detail-fill stui-mt-sm">
        <div className="stui-data-grid-container">
          <div className="stui-data-grid-scroll-area">
            <table className="stui-data-grid stui-data-grid-auto">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" className="stui-checkbox-input" checked={allShownPicked} disabled={shownPullable.length === 0}
                      onChange={toggleAllShown} title="Select every new sheet shown" />
                  </th>
                  <th>Index Sheet</th><th>Description</th><th>Mfr</th><th>Status</th><th>Datasheet</th><th>Rev</th><th>Values</th><th>Here</th><th />
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={10} className="stui-td-empty">
                    {loading ? 'Loading…' : rows.length === 0 ? 'The source has no Index Sheets.' : 'No matching results'}
                  </td></tr>
                )}
                {shown.map(r => (
                  <tr key={r.sourceId}>
                    <td>
                      {isPullable(r) && (
                        <input type="checkbox" className="stui-checkbox-input" checked={selected.has(r.sourceId)} onChange={() => toggle(r.sourceId)} />
                      )}
                    </td>
                    <td className="stui-mono">{r.indexSheet}</td>
                    <td>{r.description}</td>
                    <td title={r.manufacturer || ''}>{r.manufacturerCode}</td>
                    <td>{r.status ? <span className={`stui-badge ${statusBadgeClass(r.status)}`}>{r.status}</span> : ''}</td>
                    <td className="stui-mono" title={r.datasheetNote || ''}>
                      {r.dsIndex ?? ''}
                      {r.dsConfig != null && !r.datasheetPullable ? <span className="stui-text-warning"> (sheet only)</span> : null}
                    </td>
                    <td className="stui-mono">{r.rev ?? ''}</td>
                    <td>{r.dsConfig != null ? r.valueCount : ''}</td>
                    <td title={r.blocker || (r.fieldChanges ?? []).map(c => c.field).join(', ') || r.targetDsIndex || ''}>
                      <span className={`stui-badge ${stateBadgeClass(r.state)}`}>{stateLabel(r.state)}</span>
                    </td>
                    <td>
                      <div className="stui-row-4">
                        {r.state === 'Blocked' && <span className="stui-td-muted" title={r.blocker}>{r.blocker?.includes('grammar') ? 'Rename first' : 'Blocked'}</span>}
                        {canCompareDatasheet(r) && (
                          <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => openDiff(r)} title="Compare datasheets">
                            <Eye size={14} /> Datasheet
                          </button>
                        )}
                        {canUpdate(r) && (
                          <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setDialog({ kind: 'update', row: r })}>
                            <RotateCcw size={14} /> Update from source
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {dialog?.kind === 'pull' && (
        <PullPreviewModal sourceLabel={sourceLabel} rows={pickedRows} bringValues={bringValues} busy={busy}
          onPull={handlePull} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'results' && <PullResultsModal results={dialog.results} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'update' && (
        <UpdateFromSourceModal row={dialog.row} sourceLabel={sourceLabel} busy={busy}
          onUpdate={() => handleUpdate(dialog.row)} onClose={() => setDialog(null)} />
      )}
      {diff && (
        <DsDiffViewerModal
          {...diff}
          onClose={() => setDiff(null)}
          actions={[
            { label: 'Decline', onClick: () => setDiff(null), disabled: busy },
            ...(diff.action ? [{
              label: busy ? 'Working…' : diff.action.label, primary: true,
              disabled: busy || !!diff.blocker, onClick: handleDatasheetAction,
            }] : []),
          ]}
        />
      )}
    </>
  );
}
